import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface OptionItem<T extends string> {
  value: T;
  label: string;
}

/**
 * Styled click-to-expand dropdown that matches the project's card look.
 * No search — callers pass a small, static set of options.
 */
export function OptionPicker<T extends string>({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly OptionItem<T>[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          textAlign: "left",
          background: "var(--bg)",
          border: "1px solid var(--separator)",
          borderRadius: "var(--r-md)",
          padding: "10px 14px",
          color: current ? "var(--fg1)" : "var(--fg3)",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
          fontSize: 15,
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          {current?.label ?? placeholder ?? ""}
        </span>
        <ChevronDown
          size={18}
          color="var(--fg3)"
          style={{
            transition: "transform var(--d-fast, 150ms) ease",
            transform: open ? "rotate(180deg)" : undefined,
          }}
        />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: "var(--bg-section)",
            border: "1px solid var(--separator)",
            borderRadius: "var(--r-md)",
            boxShadow: "var(--sh-2, 0 6px 20px rgba(16,19,26,0.08))",
            overflow: "hidden",
          }}
        >
          {options.map((opt, i) => {
            const selected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  background: selected
                    ? "var(--brand-orange-ghost, rgba(255,90,31,0.08))"
                    : "transparent",
                  border: 0,
                  borderTop: i === 0 ? 0 : "1px solid var(--separator)",
                  padding: "10px 14px",
                  cursor: "pointer",
                  textAlign: "left",
                  color: selected ? "var(--brand-orange)" : "var(--fg1)",
                  fontFamily: "var(--font-body)",
                  fontSize: 15,
                  fontWeight: selected ? 600 : 400,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>{opt.label}</span>
                {selected && <Check size={16} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
